import React from 'react';
import {
    injectIntl,
    InjectedIntl,
    FormattedHTMLMessage,
    FormattedMessage,
} from 'react-intl';

import {
    VerificationService,
    ErrorId,
    ErrorResponse,
    PoweredByComponent,
    TryAgainButtonComponent,
    ExtendedErrorId,
    getLocaleSafely,
    requestOrganizationConstants as constants,
} from '@sheerid/jslib';
import { ReturnToIntuitButtonComponent } from '../ReturnToIntuitButtonComponent';
import { sendPostMessage } from '../../lib/utils/browser';
import { REPOSITION_MESSAGE } from '../../constants';


interface StepErrorProps {
    verificationService: VerificationService;
    errorId?: ErrorId | ExtendedErrorId;
    intl?: InjectedIntl;
}

const StepError = ({ verificationService, errorId }: StepErrorProps) => {
    const verificationResponse = verificationService.verificationResponse as ErrorResponse;
    const limitExceededError = verificationResponse.errorIds.includes('verificationLimitExceeded') || verificationResponse.errorIds.includes('docReviewLimitExceeded');
    const invalidSoftwareTypeError = errorId === 'invalidSoftwareType';
    const errorTitle: FormattedMessage.Props = limitExceededError
        ? {
            id: 'limitExceededError',
            defaultMessage: 'Verification Limit Exceeded',
        }
        : {
            id: 'error',
            defaultMessage: 'Error',
        };

    React.useEffect(() => {
        // Send postMessage so that Intuit's site can reposition the scroll position of the user
        sendPostMessage(REPOSITION_MESSAGE, 'https://www.intuit.com');
    }, []);

    return (
        <div id="sid-step-error" className="sid-l-container">
            <div className="sid-header">
                <div className="sid-header__title sid-l-space-top-md">
                    <FormattedHTMLMessage {...errorTitle} />
                </div>
                <div className="sid-header__subtitle sid-l-space-top-md">
                    <div className="sid-error-msg">
                        {errorId !== undefined
                            ? <FormattedHTMLMessage id={`errorId.${errorId}`} defaultMessage="Unknown Error" />
                            : null}
                    </div>
                    <div className="sid-error-msg">
                        {
                            verificationResponse.errorIds
                                ? (
                                    <p>
                                        {
                                            verificationResponse.errorIds.map(
                                                errId => (
                                                    <FormattedHTMLMessage
                                                        id={`errorId.${errId}`}
                                                        defaultMessage={errId}
                                                        values={{
                                                            feedbackUrl: constants.FEEDBACK_FORM_URL,
                                                            programId: verificationService.programId || '',
                                                            locale: getLocaleSafely(verificationService.viewModel, verificationResponse).replace('-', '_'),
                                                        }}
                                                    />
                                                ),
                                            )
                                        }
                                    </p>
                                )
                                : null
                        }
                    </div>

                    {!limitExceededError && !invalidSoftwareTypeError && (
                        <div className="sid-l-space-top-lg">
                            <TryAgainButtonComponent
                                verificationService={verificationService}
                            />
                        </div>
                    )}

                    {invalidSoftwareTypeError && (
                        <div className="sid-l-space-top-lg">
                            <ReturnToIntuitButtonComponent />
                        </div>
                    )}
                </div>
            </div>

            <div className="sid-footer sid-l-space-top-lg">
                <PoweredByComponent verificationService={verificationService} />
            </div>

        </div>
    );
};

export const StepErrorComponent = injectIntl(StepError);
